import { forwardRef, useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import { TextFieldProps } from '@mui/material';
import { DesktopDateRangePicker, DesktopDateRangePickerProps } from '@mui/x-date-pickers-pro';
import { DateRange } from '@mui/lab/DateRangePicker';
import { MasonTheme } from '@bymason-private/controller-v1-theme-augmentation';
import { Box } from '../../mui/Box';
import { Stack } from '../../mui/Stack';
import { ControllerTextField } from '../ControllerTextField';
import { MdEvent } from '../Icons';

export { DateRange } from '@mui/lab/DateRangePicker';

interface DateRangePickerProps
  extends Omit<DesktopDateRangePickerProps<Date, Date>, 'disableCloseOnSelect' | 'renderInput'> {
  endDateHelperText?: string;
  endDateLabel: string;
  required?: boolean;
  startDateHelperText?: string;
  startDateLabel: string;
  onValidate?: (isValid: boolean) => void;
}

const useStyles = makeStyles((theme: MasonTheme) => ({
  dateRangePicker: {
    '& .MuiTypography-caption': {
      color: theme.palette.grey[600],
      ...theme.typography.h6,
    },
  },
  textFieldIcon: {
    cursor: 'pointer',
    fontSize: 32,
    marginRight: theme.spacing(0.75),
    opacity: 0.5,
  },
}));

export const DateRangePicker = forwardRef<HTMLDivElement, DateRangePickerProps>(function (
  {
    className,
    endDateHelperText,
    endDateLabel,
    onChange,
    onClose,
    onOpen,
    onValidate,
    open,
    required = false,
    startDateHelperText,
    startDateLabel,
    value,
    ...props
  }: DateRangePickerProps,
  ref
) {
  const classes = useStyles();
  const [startError, setStartError] = useState('');
  const [endError, setEndError] = useState('');

  const handleRangeChange = useCallback(
    (dateRange: DateRange<Date>) => {
      const startDateInvalid = required && !dateRange[0];
      const endDateInvalid = required && !dateRange[1];
      const endDateBeforeStartDate = !!dateRange[0] && !!dateRange[1] && dateRange[0] > dateRange[1];

      setStartError(startDateInvalid ? `Invalid ${startDateLabel?.toLowerCase() || 'date'}` : '');

      if (endDateInvalid) {
        setEndError(`Invalid ${endDateLabel?.toLowerCase() || 'date'}`);
      } else if (endDateBeforeStartDate) {
        setEndError(
          `${endDateLabel?.toLowerCase() || 'end date'} before ${
            startDateLabel?.toLowerCase() || 'start date'
          }`
        );
      } else {
        setEndError('');
      }

      if (onChange) {
        onChange(dateRange);
      }

      if (onValidate) {
        onValidate(!endDateBeforeStartDate && !endDateInvalid && !startDateInvalid);
      }
    },
    [endDateLabel, onChange, onValidate, required, startDateLabel]
  );

  const handleOpenClose = useCallback(() => {
    open ? onClose && onClose() : onOpen && onOpen();
  }, [onClose, onOpen, open]);

  const textFieldEndAdornment = useMemo(
    () => <MdEvent className={classes.textFieldIcon} onClick={handleOpenClose} />,
    [classes.textFieldIcon, handleOpenClose]
  );

  return (
    <DesktopDateRangePicker
      {...props}
      className={clsx(classes.dateRangePicker, className)}
      closeOnSelect={false}
      onAccept={onClose}
      onChange={handleRangeChange}
      onClose={onClose}
      open={open}
      ref={ref}
      renderInput={(startProps: TextFieldProps, endProps: TextFieldProps) => {
        return (
          <Stack alignItems='center' direction='row' width='100%'>
            <ControllerTextField
              error={!!startError}
              errorText={startError}
              helperText={startDateHelperText}
              inputProps={startProps.inputProps}
              inputRef={startProps.inputRef}
              label={startDateLabel}
              onFocus={handleOpenClose}
              // ref={startProps.ref}
              required={required}
              value={value[0]}
              autoComplete='date'
              InputProps={{
                endAdornment: textFieldEndAdornment,
                ref: startProps.ref,
              }}
            />
            <Box sx={{ mx: 2 }}> to </Box>
            <ControllerTextField
              error={!!endError}
              errorText={startError}
              helperText={endDateHelperText}
              inputProps={endProps.inputProps}
              inputRef={endProps.inputRef}
              label={endDateLabel}
              onFocus={handleOpenClose}
              // ref={endProps.ref}
              required={required}
              value={value[1]}
              autoComplete='date'
              InputProps={{
                endAdornment: textFieldEndAdornment,
                ref: endProps.ref,
              }}
            />
          </Stack>
        );
      }}
      value={value}
    />
  );
});

DateRangePicker.displayName = 'DateRangePicker';
